import type { SxProps, Theme } from '@mui/material';
import { brandColors, APP_FONT_FAMILY, brandCss, TYPE } from './appTheme';

/** Table body — 13px / table header uses TYPE.tableHeader */
export const TABLE_FONT_SIZE = TYPE.tableContent.fontSize;
export const TABLE_HEADER_FONT_SIZE = TYPE.tableHeader.fontSize;

/** Brand color from tenant settings (ThemeContext sets --brand-primary) */
export const brandPrimary = `var(--brand-primary, ${brandCss.primary})`;
export const brandTableBorder = `var(--brand-table-border, ${brandColors.border})`;
export const brandTableBorderStrong = `var(--brand-table-border-strong, ${brandCss.primary})`;

/** Outer wrapper for any data table panel */
export const tableWrapperSx: SxProps<Theme> = {
  border: `1px solid ${brandTableBorder}`,
  borderRadius: 2,
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  width: '100%',
  maxWidth: '100%',
};

/** Solid brand header (matches consultation-style tables; color from theme preset). */
export const tableHeadRowSx: SxProps<Theme> = {
  background: `var(--brand-table-header, ${brandCss.primary})`,
  boxShadow: 'none',
  '& th': {
    borderBottom: 'none',
    color: 'var(--brand-table-header-text, #ffffff) !important',
  },
};

export const tableHeadCellSx = (options?: { align?: 'left' | 'right' | 'center'; width?: number | string }): SxProps<Theme> => ({
  fontWeight: 700,
  fontSize: 11,
  lineHeight: '16px',
  fontFamily: APP_FONT_FAMILY,
  textTransform: 'none',
  letterSpacing: '0.02em',
  color: 'var(--brand-table-header-text, #ffffff)',
  bgcolor: 'transparent',
  py: '8px',
  px: { xs: 1, sm: 1.25 },
  borderBottom: 'none',
  borderRight: 'none',
  whiteSpace: 'nowrap',
  width: options?.width,
  minWidth: options?.width ? undefined : 88,
  textAlign: options?.align,
  position: 'relative',
  '&:not(:last-of-type)::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '30%',
    height: '40%',
    width: '1px',
    bgcolor: 'rgba(255,255,255,0.22)',
  },
});

export const tableSortLabelSx: SxProps<Theme> = {
  color: 'var(--brand-table-header-text, #ffffff) !important',
  fontWeight: 700,
  fontSize: 11,
  lineHeight: '16px',
  fontFamily: APP_FONT_FAMILY,
  flexDirection: 'row',
  gap: 0.25,
  transition: 'color 0.15s ease',
  '&:hover': {
    color: 'var(--brand-table-header-text, #ffffff) !important',
    '& .MuiTableSortLabel-icon': { opacity: 0.95, color: 'var(--brand-table-header-text, #ffffff)' },
  },
  '&.Mui-active': {
    color: 'var(--brand-table-header-text, #ffffff) !important',
    '& .MuiTableSortLabel-icon': {
      color: 'var(--brand-table-header-text, #ffffff) !important',
      opacity: 1,
    },
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: 14,
    opacity: 0,
    color: 'var(--brand-table-header-text, #ffffff)',
    transition: 'opacity 0.15s ease, color 0.15s ease, transform 0.15s ease',
  },
  '&:hover .MuiTableSortLabel-icon': {
    opacity: 0.85,
  },
};

export const tableHeadTextSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 11,
  lineHeight: '16px',
  fontFamily: APP_FONT_FAMILY,
  letterSpacing: '0.02em',
  color: 'var(--brand-table-header-text, #ffffff)',
};

export const tableBodyCellSx: SxProps<Theme> = {
  py: '8px',
  px: { xs: 1, sm: 1.25 },
  fontSize: TABLE_FONT_SIZE,
  fontFamily: APP_FONT_FAMILY,
  fontWeight: 500,
  lineHeight: TYPE.tableContent.lineHeight,
  color: brandColors.textDark,
  borderBottom: `1px solid ${brandColors.border}`,
  borderRight: 'none',
  verticalAlign: 'middle',
  maxWidth: { xs: 220, sm: 280, md: 360 },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const dashboardTableBodyCellSx = tableBodyCellSx;

export const dashboardTableHeadCellSx: SxProps<Theme> = {
  ...tableHeadCellSx(),
  py: 0.5,
  px: 1,
  fontSize: TABLE_HEADER_FONT_SIZE,
};

export const tableContainerSx: SxProps<Theme> = {
  maxHeight: { xs: 'calc(100dvh - 260px)', sm: 'calc(100dvh - 240px)', md: 'calc(100dvh - 220px)' },
  overflowX: 'auto',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  width: '100%',
  maxWidth: '100%',
  border: 'none',
  borderRadius: 0,
  '& .MuiTable-root': {
    minWidth: 640,
    width: '100%',
  },
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
};

export const tableRowHoverSx: SxProps<Theme> = {
  transition: 'background 0.15s',
  '&:hover': { bgcolor: 'var(--brand-green50, rgba(98,33,214,0.04))' },
};
